import {CaretDownFilled} from '@ant-design/icons';
import {Card, Dropdown, Menu, Space, Spin} from 'antd';
import {Format, formatValue, formatCompact} from 'common/util/Util'
import dayjs from 'dayjs';
import ReactECharts, {EChartsOption} from 'echarts-for-react';
import React, {useMemo, useState} from 'react';
import {PerformanceData} from './UseDashboardData.ts';
import { useUserContext } from 'common/context/UserContext.tsx'

interface PerformanceChartProps {
    performanceData?: PerformanceData;
    loading?: boolean;
}

const metrics = {
    costs: {label: 'Cost', format: Format.Cost},
    costGross: {label: 'Cost Gross', format: Format.Cost},
    costNet: {label: 'Cost Net', format: Format.Cost},
    profit: {label: 'Profit', format: Format.Cost},
    impressions: {label: 'Impressions', format: Format.Int},
    clicks: {label: 'Clicks', format: Format.Int},
    ctr: {label: 'CTR', format: Format.Percent},
    trackers: {label: 'Events', format: Format.Int},
    ecpm: {label: 'eCPM', format: Format.Cost}
}

const metricOrder = ['costNet', 'costGross', 'profit', 'impressions', 'clicks', 'ctr', 'trackers', 'ecpm', 'costs'];

const PerformanceChart: React.FC<PerformanceChartProps> = ({performanceData, loading}) => {
  const { colorPalette } = useUserContext()

    const colors = [
      colorPalette['--color-primary-1'] ?? '#3B82F6',
      colorPalette['--color-secondary-1'] ?? '#F59E0B',
      colorPalette['--color-system-5'] ?? '#10B981',
      colorPalette['--color-system-3'] ?? '#EF4444',
      colorPalette['--color-primary-2'] ?? '#6366F1',
      colorPalette['--color-secondary-2'] ?? '#EC4899'
    ];
    const timestamps = performanceData?.dateTime ?? [];

    const availableMetrics = useMemo(() => {
        if (!performanceData) {
            return [];
        }
        const keys = Object.keys(performanceData).filter((key) => key !== 'dateTime' && Array.isArray(performanceData[key as keyof PerformanceData]));
        return metricOrder.filter((metric) => keys.includes(metric));
    }, [performanceData]);

    const defaultSelectedMetrics = useMemo(() => {
        const primaryMetric = availableMetrics.includes('costNet') ? 'costNet' : 'costs';
        const secondaryMetric = availableMetrics.includes('impressions') ? 'impressions' : availableMetrics.find((metric) => metric !== primaryMetric);
        return [primaryMetric, secondaryMetric].filter(Boolean) as string[];
    }, [availableMetrics]);

    const [customSelectedMetrics, setCustomSelectedMetrics] = useState<string[]>();
    const selectedMetrics = customSelectedMetrics?.every((metric) => availableMetrics.includes(metric))
        ? customSelectedMetrics
        : defaultSelectedMetrics;

    const hasTimePart = timestamps.some((ts) => {
        const d = dayjs(ts);
        return d.hour() !== 0 || d.minute() !== 0 || d.second() !== 0;
    });

    const spanInDays = timestamps.length > 1 ? dayjs(timestamps[timestamps.length - 1]).diff(dayjs(timestamps[0]), 'day') : 0;

    const xAxisDateFormat = hasTimePart && spanInDays > 0 ? 'D MMM HH:mm' : hasTimePart ? 'HH:mm' : 'D MMM';

    const handleMetricReplace = (index: number, newMetric: string) => {
        const newSelection = [...selectedMetrics];
        newSelection[index] = newMetric;
        setCustomSelectedMetrics(newSelection);
    };

    const renderMetricSelector = (metric: string, index: number) => {
        const menu = (
            <Menu
                onClick={({key}) => handleMetricReplace(index, key)}
                items={availableMetrics
                    .filter((m) => !selectedMetrics.includes(m) || m === metric)
                    .map((m) => ({
                        key: m,
                        label: metrics[m]?.label ?? m
                    }))}
            />
        );

        return (
            <Dropdown overlay={menu} trigger={['click']} key={metric}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 12,
                        cursor: 'pointer',
                        userSelect: 'none'
                    }}
                >
                    <div
                        style={{
                            width: 10,
                            height: 10,
                            backgroundColor: colors[index % colors.length],
                            marginRight: 8,
                            borderRadius: 2
                        }}
                    />
                    <span style={{fontWeight: 500}}>{metrics[metric]?.label ?? metric}</span>
                    <CaretDownFilled style={{fontSize: 12, marginLeft: 4}}/>
                </div>
            </Dropdown>
        );
    };

    const option: EChartsOption = {
        tooltip: {
            trigger: 'axis' as const,
            formatter: (params: any) => {
                const index = params[0].dataIndex;
                const date = timestamps[index]; // original timestamp
                const formattedDate = dayjs(date).format('YYYY-MM-DD HH:mm');

                // Build custom lines
                const lines = params.map((p: any, i: number) => {
                    const metric = selectedMetrics[i];
                    const valueArray = performanceData?.[metric as keyof PerformanceData];
                    const originalValue = Array.isArray(valueArray) && typeof valueArray[index] === 'number' ? (valueArray[index] as number) : 0;
                    const absolute = formatValue(metrics[metric]?.format, originalValue) // formatted raw value

                    return `<div>
                            <span style="display:inline-block;margin-right:4px;border-radius:2px;width:10px;height:10px;background:${p.color};"></span>
                            <strong>${p.seriesName}</strong>: ${absolute}
                            </div>`
                })
                return `<div><strong>${formattedDate}</strong><br/>${lines.join('')}</div>`
            }
        },
        legend: {
            show: false
        },
        grid: {
            top: 10,
            left: 80,
            right: 80,
            bottom: 60
        },
        xAxis: {
            type: 'category' as const,
            boundaryGap: false,
            data: timestamps.map((ts) => dayjs(ts).format(xAxisDateFormat)),
            splitLine: {
                show: true,
                lineStyle: {
                    type: 'dashed',
                    color: '#ddd'
                },
                interval: timestamps.length > 40 ? 'auto' : 0
            },
            axisLabel: {
                interval: timestamps.length > 40 ? 'auto' : 0,
                hideOverlap: true
            },
            axisTick: {
                alignWithLabel: true,
                interval: timestamps.length > 40 ? 'auto' : 0
            }
        },
        yAxis: [
            {
                type: 'value' as const,
                name: metrics[selectedMetrics[0]].label,
                position: 'left',
                axisLabel: {
                    formatter: (value: number, _: number) => {
                        const raw = performanceData?.[selectedMetrics[0] as keyof PerformanceData] as number[]
                        if (!raw) {
                            return ''
                        }
                        const max = Math.max(...raw)
                        return formatCompact(metrics[selectedMetrics[0]].format, value * max)
                    }
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        type: 'dashed',
                        color: '#ddd'
                    }
                }
            },
            {
                type: 'value' as const,
                name: metrics[selectedMetrics[1]].label,
                position: 'right',
                axisLabel: {
                    formatter: (value: number, _: number) => {
                        const raw = performanceData?.[selectedMetrics[1] as keyof PerformanceData] as number[];
                        if (!raw) {
                            return ''
                        }
                        const max = Math.max(...raw)
                        return formatCompact(metrics[selectedMetrics[1]].format, value * max)
                    }
                },
                splitLine: {show: false}
            }
        ],

        series: selectedMetrics.map((metric, index) => {
            const value = performanceData?.[metric as keyof PerformanceData] ?? [];
            const rawData: number[] = Array.isArray(value) && value.every((v) => typeof v === 'number') ? (value as number[]) : [];
            const maxVal = Math.max(...rawData.map((v) => Number(v) || 0));
            const normalizedData = maxVal > 0 ? rawData.map((v) => (Number(v) || 0) / maxVal) : rawData;

            return {
                name: metrics[metric].label ?? metric,
                type: 'line' as const,
                smooth: false,
                showSymbol: false,
                data: timestamps.map((_, i) => normalizedData?.[i]) as number[],
                yAxisIndex: index, // <--- assign axis index: 0 (left), 1 (right)
                lineStyle: {color: colors[index % colors.length]},
                itemStyle: {color: colors[index % colors.length]}
            };
        })
    };

    return (
        <Card style={{borderRadius: 8, background: '#FFFFFF', width: '100%', margin: 8}}>
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 16}}>
                <div style={{fontSize: 16, fontWeight: 600, paddingLeft: 4}}>Performance</div>

                <Space>{selectedMetrics.map((metric, index) => renderMetricSelector(metric, index))}</Space>
            </div>

            {loading ? (
                <Spin size="large"
                      style={{display: 'block', margin: '20px', minHeight: '380px', alignContent: 'center'}}/>
            ) : (<ReactECharts key={selectedMetrics.join(',')} option={option} style={{height: '400px', width: '100%'}}
                               notMerge={true} lazyUpdate={true}/>)}
        </Card>
    );
};

export default PerformanceChart;
